import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InlineNotification, Link, RadioGroup, RadioGroupOption, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { AuthWrapper, OtpInput } from '../../components';
import { AuthContext, AuthRoute } from '../../features';
import { maskEmail, maskPhone } from '../../features/auth/utils';
import { useCooldown } from '../../hooks';
const RESEND_COOLDOWN_SECONDS = 30;
export const MfaChallengePage = () => {
    const translate = useLocalizeMessage();
    const navigate = useNavigate();
    const { addNotification } = useToastNotification();
    const { mfaChallenge, sendMfaChallenge, verifyMfaChallenge, clearMfaChallenge, redirectToDefaultPage, isLoading, isLoggedIn, } = useContext(AuthContext);
    const [step, setStep] = useState('method_selection');
    const [selectedMethod, setSelectedMethod] = useState(undefined);
    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [cooldown, setCooldown] = useCooldown();
    const [error, setError] = useState(null);
    const hasAutoSelected = useRef(false);
    const enrolledMethods = useMemo(() => mfaChallenge?.mfaMethods || [], [mfaChallenge]);
    // Handle states where there is no MFA challenge:
    // - Logged in (post-verification): redirect to default page
    // - Not logged in (stale visit): redirect to login
    useEffect(() => {
        if (!mfaChallenge) {
            if (isLoggedIn) {
                void redirectToDefaultPage();
            }
            else {
                navigate(AuthRoute.LOGIN);
            }
        }
    }, [mfaChallenge, isLoggedIn, navigate, redirectToDefaultPage]);
    const handleSendChallenge = useCallback(async (method) => {
        const targetMethod = method || selectedMethod;
        if (!targetMethod || isSending) {
            return;
        }
        setIsSending(true);
        setError(null);
        try {
            await sendMfaChallenge(targetMethod.id);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        }
        catch (err) {
            if (err instanceof AxiosError && err.response?.status === 401) {
                return;
            }
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to send verification code. Please try again.'),
                type: 'error',
            });
            throw err;
        }
        finally {
            setIsSending(false);
        }
    }, [selectedMethod, isSending, sendMfaChallenge, addNotification, translate]);
    // If only one method enrolled, auto-select and go to confirmation
    useEffect(() => {
        if (mfaChallenge && enrolledMethods.length === 1 && !hasAutoSelected.current) {
            hasAutoSelected.current = true;
            setSelectedMethod(enrolledMethods[0]);
            if (enrolledMethods[0].type === 'totp') {
                setStep('code_verification');
            }
            else {
                void handleSendChallenge(enrolledMethods[0])
                    .then(() => {
                    setStep('code_verification');
                })
                    .catch(() => {
                    // Error already shown via toast in handleSendChallenge
                });
            }
        }
        else if (mfaChallenge && enrolledMethods.length > 1) {
            setSelectedMethod(enrolledMethods[0]);
        }
        // Intentionally exclude handleSendChallenge to avoid resetting a user-selected method
        // when callback identity changes during normal render cycles.
    }, [enrolledMethods, mfaChallenge]);
    const handleNextFromMethodSelection = async () => {
        if (!selectedMethod) {
            return;
        }
        if (selectedMethod.type === 'totp') {
            setStep('code_verification');
        }
        else {
            try {
                await handleSendChallenge();
                setStep('code_verification');
            }
            catch {
                // Error already shown via toast in handleSendChallenge
            }
        }
    };
    const handleResend = async () => {
        setCode('');
        setError(null);
        await handleSendChallenge();
    };
    const handleVerify = async () => {
        if (!code.trim()) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await verifyMfaChallenge(code.trim());
        }
        catch {
            setError(translate('Incorrect code entered'));
            setCode('');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleBack = () => {
        setError(null);
        setCode('');
        if (step === 'code_verification') {
            if (enrolledMethods.length === 1) {
                clearMfaChallenge();
                navigate(AuthRoute.LOGIN);
            }
            else {
                // Multiple methods: go back to method selection
                setStep('method_selection');
            }
        }
    };
    const getMethodLabel = (method) => {
        switch (method.type) {
            case 'email':
                return mfaChallenge?.loginEmail
                    ? `${translate('Email to')} ${maskEmail(mfaChallenge.loginEmail)}`
                    : method.identifier
                        ? `${translate('Email to')} ${method.identifier}`
                        : translate('Email');
            case 'sms':
                return method.identifier
                    ? `${translate('Text to')} ${maskPhone(method.identifier)}`
                    : translate('Text message (SMS)');
            case 'totp':
                return translate('Authenticator app');
            default:
                return method.type;
        }
    };
    const getTitle = () => {
        if (step === 'code_verification' && selectedMethod?.type === 'totp') {
            return translate('Identity verification');
        }
        return translate('Identity verification');
    };
    if (!mfaChallenge) {
        return (_jsx(AuthWrapper, { title: translate('Identity verification'), children: _jsx(Spinner, {}) }));
    }
    return (_jsxs(AuthWrapper, { title: getTitle(), onSubmit: step === 'code_verification' ? handleVerify : undefined, showSupportFooter: true, children: [step === 'method_selection' && enrolledMethods.length > 1 && (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsxs(Text, { size: 'sm', className: 'text-secondary text-center', children: [_jsx("span", { className: 'font-semibold', children: translate('Choose a verification method.') }), ' ', translate("Select how you'd like to receive your verification code.")] }), _jsx(RadioGroup, { value: selectedMethod, onChange: (value) => setSelectedMethod(value), children: enrolledMethods.map((method) => (_jsx(RadioGroupOption, { option: {
                                value: method,
                                label: getMethodLabel(method),
                            } }, method.id))) }), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-challenge-next-button', label: translate('Next'), onClick: handleNextFromMethodSelection, disabled: !selectedMethod || isLoading })] })), step === 'code_verification' && (_jsxs("div", { className: 'flex flex-col gap-6', children: [enrolledMethods.length > 1 && (_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] })), error && _jsx(InlineNotification, { type: 'error', title: error }), _jsx(Text, { size: 'sm', className: 'text-secondary text-left', children: selectedMethod?.type === 'totp'
                            ? translate('Please enter the one-time code displayed in your authenticator app')
                            : `${translate('A verification code has been sent to')} ${selectedMethod?.identifier || ''}.\n${translate('Please enter the one-time code to finish signing in.')}` }), _jsx(OtpInput, { value: code, onChange: setCode, hasError: !!error }), selectedMethod?.type !== 'totp' && (_jsxs("div", { className: 'flex items-center justify-center gap-2', children: [_jsx(Text, { size: 'sm', className: 'text-secondary', children: translate("Haven't received a code?") }), _jsx(Link, { to: '#', onClick: handleResend, disabled: cooldown > 0 || isSending, children: cooldown > 0 ? `${translate('Resend new')} (${cooldown}s)` : translate('Resend new') })] })), _jsx(Button, { className: 'w-full', dataTestId: 'mfa-challenge-verify-button', label: translate('Submit'), disabled: code.length < 6 || isSubmitting || isLoading })] }))] }));
};
